import { ToggleButton, ToggleButtonGroup } from "@heroui/react";
import { useMemo } from "react";

import { useTranslations } from "@/src/i18n/i18n";
import type { ReportCounts } from "@/src/pages/Reports/hooks/useReportCounts";
import { STATUS_KEY } from "@/src/pages/Reports/reportLabels";
import type { ReportStatus } from "@/src/pages/Reports/reports.type";
import {
  isReportStatus,
  REPORT_STATUSES,
} from "@/src/pages/Reports/reports.type";

type ReportsFilterProps = {
  status: ReportStatus;
  counts: ReportCounts;
  onChange: (_status: ReportStatus) => void;
};

/**
 * Les trois files : en attente, retenus, classés sans suite.
 *
 * Un groupe de bascules à sélection unique plutôt que des onglets : il n'y a
 * qu'un seul panneau, que le filtre re-remplit. Des `role="tab"` promettraient
 * des panneaux distincts à parcourir au clavier, ce qui serait faux.
 *
 * `disallowEmptySelection` : sans lui, recliquer le filtre actif le désactive
 * et l'écran n'a plus de statut du tout — un état que la requête ne sait pas
 * représenter.
 *
 * ⚠️ **Le compteur fait partie du nom accessible**, et ce n'est pas un oubli :
 * « Retenus 12 » est ce qui fait de la bascule une file plutôt qu'un filtre.
 * Le masquer aux lecteurs d'écran leur retirerait la seule information qui dit
 * s'il y a quelque chose à aller voir.
 */
export function ReportsFilter({
  status,
  counts,
  onChange,
}: ReportsFilterProps) {
  const t = useTranslations("reports");
  const ta = useTranslations("a11y");

  // Mémoïsé : reconstruit à chaque rendu, le tableau change d'identité et
  // relance la réconciliation du groupe pour rien.
  const selectedKeys = useMemo(() => [status], [status]);

  return (
    <ToggleButtonGroup
      aria-label={ta("filter-reports")}
      disallowEmptySelection
      selectedKeys={selectedKeys}
      selectionMode="single"
      size="sm"
      onSelectionChange={(keys) => {
        // `Key` vaut `string | number` : le groupe ne connaît pas notre
        // énumération, la garde la lui réapprend.
        for (const key of keys) {
          if (isReportStatus(key)) onChange(key);
        }
      }}
    >
      {REPORT_STATUSES.map((value) => (
        <ToggleButton id={value} key={value}>
          {t(STATUS_KEY[value])}

          {/* `opacity` et non `text-muted` : la bascule sélectionnée écrit sur
              un fond d'accent, où une couleur figée passerait sous le seuil de
              contraste. L'opacité, elle, suit la couleur du texte courant. */}
          {counts[value] === undefined ? null : (
            <span className="ml-1.5 tabular-nums opacity-60">
              {counts[value]}
            </span>
          )}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
